import { getFriendlyDateTime, Loading } from "@vellum/ui";
import type { SubscriptionSendHistorySchema } from "@vellum/vellum-api";
import { useEffect, useState } from "react";

import { vellumApiSdk } from "@vellum/micro-app-auth";

export function SendHistorySection({
  subscriptionId,
  refreshKey,
}: {
  subscriptionId: string;
  /** Bump this to force a re-fetch (e.g. after an on-demand send). */
  refreshKey?: number;
}) {
  const [histories, setHistories] = useState<SubscriptionSendHistorySchema[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    vellumApiSdk
      .getSubscriptionHistory({ path: { subscription_id: subscriptionId } })
      .then((res) => {
        setHistories(res.data ?? []);
      })
      .catch(() => {
        setError("Something went wrong loading send history.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [subscriptionId, refreshKey]);

  return (
    <div className="border-t border-gray-200 bg-white px-4 py-3">
      <h2 className="text-sm font-semibold text-gray-900">
        Subscription send history
      </h2>
      {loading ? (
        <div className="flex justify-center py-4">
          <Loading />
        </div>
      ) : error ? (
        <p className="mt-2 text-sm text-error">{error}</p>
      ) : histories.length === 0 ? (
        <p className="mt-2 text-sm text-gray-500">
          This report has not been sent yet.
        </p>
      ) : (
        <div className="mt-2 overflow-hidden rounded-md border border-gray-200">
          <table className="w-full divide-y divide-gray-200 text-sm">
            <thead>
              <tr>
                <th className="max-w-[10rem] px-4 py-2 text-left text-xs font-medium">
                  Sent At
                </th>
                <th className="max-w-xs px-4 py-2 text-left text-xs font-medium">
                  Message
                </th>
                <th className="max-w-xs px-4 py-2 text-left text-xs font-medium">
                  Recipients
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {histories.map((history) => (
                <tr key={history.id}>
                  <td className="max-w-[10rem] break-words px-4 py-2 align-top">
                    {getFriendlyDateTime(history.created_at)}
                  </td>
                  <td className="max-w-xs break-words px-4 py-2 align-top truncate">
                    {history.body}
                  </td>
                  <td className="max-w-xs break-words px-4 py-2 align-top ">
                    {history.to_emails.join(", ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
